/**
 * /practice/$area — per-practice-area depth page.
 *
 * Hand-authored content from src/data/practice/areas.ts (scope, docs, steps,
 * cautions) plus src/data/practice/depth.ts (answer-first, comparison table,
 * FAQ, audience). No AI call at build or request time.
 */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";
import { PRACTICE_AREAS, type PracticeArea } from "@/data/practice/areas";
import { PRACTICE_DEPTH } from "@/data/practice/depth";
import type { PracticeDepth } from "@/data/practice/depth-types";
import { AnswerFirst } from "@/components/AnswerFirst";
import { SmartImage } from "@/components/SmartImage";
import { FirmCta } from "@/components/firm/FirmCta";
import { QaHubCta } from "@/components/QaHubCta";
import { LAWFIRM_QA } from "@/data/lawfirm/questions";
import { OfficialServiceSources } from "@/components/OfficialServiceSources";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  faqPageSchema,
  howToSchema,
} from "@/lib/schema";

import heroLitigation from "@/assets/firm-litigation-prep.jpg";
import heroCorporate from "@/assets/firm-corporate-consult.jpg";
import heroFamily from "@/assets/firm-family-estate.jpg";
import heroEstate from "@/assets/practice-estate.jpg";
import heroProperty from "@/assets/practice-property.jpg";
import heroLabour from "@/assets/practice-labour.jpg";
import heroTax from "@/assets/practice-tax.jpg";
import heroTeam from "@/assets/firm-hero-team.jpg";

const HERO: Record<string, string> = {
  litigation: heroLitigation,
  corporate: heroCorporate,
  family: heroFamily,
  estate: heroEstate,
  property: heroProperty,
  labour: heroLabour,
  tax: heroTax,
  ip: heroCorporate,
  "notary-international": heroTeam,
  pdpa: heroLabour,
};

const AREA_BY_SLUG: Record<string, PracticeArea> = Object.fromEntries(
  PRACTICE_AREAS.map((a) => [a.slug, a]),
);

type Loaded = { area: PracticeArea; depth: PracticeDepth; path: string };

export const Route = createFileRoute("/practice/$area")({
  loader: ({ params }) => {
    const area = AREA_BY_SLUG[params.area];
    const depth = PRACTICE_DEPTH[params.area];
    if (!area || !depth) throw notFound();
    return { area, depth, path: `/practice/${params.area}` } satisfies Loaded;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "ไม่พบสายงานที่ระบุ" }, { name: "robots", content: "noindex" }],
      };
    }
    const { area, depth, path } = loaderData;
    const url = absoluteUrl(path, null, "th");
    return {
      meta: [
        { title: ctrTitle(depth.seoTitle) },
        { name: "description", content: ctrDesc(depth.seoDesc) },
        { property: "og:title", content: ctrTitle(depth.seoTitle) },
        { property: "og:description", content: ctrDesc(depth.seoDesc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...canonicalAndHreflang(path, null, "th").filter((l) => l.rel === "alternate"),
      ],
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: path, title: depth.seoTitle, description: depth.seoDesc, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: absoluteUrl("/", null, "th") },
              { name: "สายงานกฎหมาย", url: absoluteUrl("/practice", null, "th") },
              { name: area.name, url },
            ]),
            legalServiceSchema({
              name: area.name,
              description: area.tagline,
              url: path,
              areaServed: "TH",
              serviceType: area.name,
            }),
            howToSchema({
              name: `ขั้นตอนการทำงานด้าน${area.name}`,
              description: area.tagline,
              steps: area.steps.map((s, i) => ({ name: `ขั้นที่ ${i + 1}`, text: s })),
            }),
            faqPageSchema(depth.faqs),
          ]),
        ),
      ],
    };
  },
  component: PracticeAreaPage,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <h1 className="text-2xl font-bold text-foreground">ไม่พบสายงานที่ระบุ</h1>
      <p className="mt-4 text-muted-foreground">
        กลับไปที่{" "}
        <Link to="/practice" className="text-primary underline">
          หน้ารวมสายงานกฎหมาย
        </Link>{" "}
        เพื่อเลือกหัวข้อที่ต้องการ
      </p>
    </main>
  ),
});

function PracticeAreaPage() {
  const { area, depth } = Route.useLoaderData();
  const hero = HERO[area.slug] ?? heroTeam;
  const related = depth.related
    .map((slug) => AREA_BY_SLUG[slug])
    .filter((a): a is PracticeArea => Boolean(a));
  const scoped = LAWFIRM_QA.filter((q) => q.topicSlug === area.slug);
  const areaQa = scoped.length >= 12 ? scoped : LAWFIRM_QA;

  return (
    <main>
      <header
        className="border-b border-border/60 text-[var(--primary-foreground)]"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-12 md:py-16">
          <nav aria-label="เส้นทางหน้า" className="text-xs text-[var(--primary-foreground)]/70">
            <Link to="/" className="hover:underline">
              หน้าแรก
            </Link>{" "}
            <span aria-hidden>/</span>{" "}
            <Link to="/practice" className="hover:underline">
              สายงานกฎหมาย
            </Link>{" "}
            <span aria-hidden>/</span> {area.name}
          </nav>
          <div className="mt-6 grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <h1 className="text-3xl font-bold leading-tight tracking-tight md:text-4xl">{area.name}</h1>
              <p className="mt-3 text-sm font-semibold text-[var(--gold)]">{area.tagline}</p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm font-semibold">
                <Link
                  to="/contact"
                  className="inline-flex min-h-[44px] items-center rounded-md bg-[var(--gold)] px-5 text-[var(--primary)]"
                >
                  ปรึกษาทนายเรื่องนี้
                </Link>
                <Link
                  to="/practice"
                  className="inline-flex min-h-[44px] items-center rounded-md border border-[var(--gold)]/60 px-5 text-[var(--gold)]"
                >
                  ดูสายงานทั้งหมด
                </Link>
              </div>
            </div>
            <SmartImage
              src={hero}
              alt={depth.imageAlt}
              width={1200}
              height={800}
              priority
              weight="hero"
              className="w-full rounded-xl border border-[var(--gold)]/30 object-cover"
            />
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-12">
        <AnswerFirst>{depth.answer}</AnswerFirst>

        <section aria-labelledby="audience" className="mt-12">
          <h2 id="audience" className="text-xl font-bold text-foreground md:text-2xl">
            หน้านี้เขียนสำหรับใคร
          </h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
            {depth.audience.map((s) => (
              <li key={s} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="scope" className="mt-12 grid gap-8 md:grid-cols-2">
          <div>
            <h2 id="scope" className="text-xl font-bold text-foreground md:text-2xl">
              ขอบเขตงานที่สำนักงานทำจริง
            </h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
              {area.scope.map((s) => (
                <li key={s} className="flex gap-2">
                  <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground md:text-2xl">ตัวอย่างเรื่องที่ลูกความนำมา</h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
              {area.examples.map((s) => (
                <li key={s} className="flex gap-2">
                  <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section aria-labelledby="docs" className="mt-12 rounded-xl border border-border bg-card p-6">
          <h2 id="docs" className="text-xl font-bold text-foreground md:text-2xl">
            เอกสารที่ควรเตรียมในรอบแรก
          </h2>
          <ul className="mt-4 grid gap-2 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
            {area.docs.map((s) => (
              <li key={s} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            หากเอกสารบางฉบับยังไม่มี ส่งเท่าที่มีให้ทีมงานตรวจก่อนได้ ทนายจะแจ้งว่าต้องขอคัดสำเนาจากหน่วยงานใด
            และฉบับใดต้องแปลหรือรับรองก่อนใช้
          </p>
        </section>

        <section aria-labelledby="steps" className="mt-12">
          <h2 id="steps" className="text-xl font-bold text-foreground md:text-2xl">
            ขั้นตอนการทำงานจริง
          </h2>
          <ol className="mt-4 space-y-3">
            {area.steps.map((s, i) => (
              <li key={s} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-xs font-bold text-[var(--gold)]">
                  {i + 1}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="cautions" className="mt-12">
          <h2 id="cautions" className="text-xl font-bold text-foreground md:text-2xl">
            ข้อควรระวังและความเข้าใจผิดที่พบบ่อย
          </h2>
          <div className="mt-4 space-y-3">
            {area.cautions.map((c) => (
              <div key={c.title} className="rounded-lg border-l-4 border-[var(--gold)] bg-muted/50 p-4">
                <p className="text-sm font-semibold text-foreground">{c.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="compare" className="mt-12">
          <h2 id="compare" className="text-xl font-bold text-foreground md:text-2xl">
            {depth.compare.heading}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{depth.compare.intro}</p>
          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead className="bg-muted/60">
                <tr>
                  <th scope="col" className="p-3 font-semibold text-foreground">
                    ประเด็น
                  </th>
                  {depth.compare.columns.map((c) => (
                    <th key={c} scope="col" className="p-3 font-semibold text-foreground">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {depth.compare.rows.map((r) => (
                  <tr key={r.label} className="border-t border-border align-top">
                    <th scope="row" className="p-3 font-medium text-foreground">
                      {r.label}
                    </th>
                    {r.cells.map((cell, i) => (
                      <td key={`${r.label}-${i}`} className="p-3 leading-relaxed text-muted-foreground">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{depth.compare.note}</p>
        </section>

        <section aria-labelledby="handoff" className="mt-12 rounded-xl border border-[var(--gold)]/40 bg-muted/40 p-6">
          <h2 id="handoff" className="text-xl font-bold text-foreground md:text-2xl">
            ให้สำนักงานดูแลทั้งเรื่อง
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{area.handoff}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm font-semibold">
            <Link to="/contact" className="text-primary hover:underline">
              ปรึกษาทนายเรื่องนี้ →
            </Link>
            {area.links.map((l) => (
              <Link key={l.to} to={l.to} className="text-primary hover:underline">
                {l.label}
              </Link>
            ))}
          </div>
        </section>

        <section aria-labelledby="faq" className="mt-12">
          <h2 id="faq" className="text-xl font-bold text-foreground md:text-2xl">
            คำถามที่พบบ่อยเรื่อง{area.name}
          </h2>
          <dl className="mt-6 space-y-5">
            {depth.faqs.map((f) => (
              <div key={f.question} className="rounded-xl border border-border bg-card p-5">
                <dt className="text-base font-semibold text-foreground">{f.question}</dt>
                <dd className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="related" className="mt-12">
          <h2 id="related" className="text-xl font-bold text-foreground md:text-2xl">
            สายงานที่มักทำร่วมกับเรื่องนี้
          </h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-3">
            {related.map((r) => (
              <li key={r.slug} className="rounded-xl border border-border bg-card p-4">
                <Link
                  to="/practice/$area"
                  params={{ area: r.slug }}
                  className="font-semibold text-foreground hover:underline"
                >
                  {r.name}
                </Link>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{r.tagline}</p>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold">
            <Link to="/practice" className="text-primary hover:underline">
              สายงานกฎหมายทั้งหมด →
            </Link>
            <Link to="/coverage" className="text-primary hover:underline">
              พื้นที่ให้บริการทั่วไทย →
            </Link>
            <Link to="/contact" className="text-primary hover:underline">
              ติดต่อสำนักงาน →
            </Link>
          </div>
        </section>

        <div className="mt-12">
          <OfficialServiceSources theme="notary" />
        </div>
      </div>

      <div className="border-t border-border/60">
        <QaHubCta base="/practice" label={`งาน${area.name}และการจ้างสำนักงานกฎหมาย`} qa={areaQa} />
      </div>

      <FirmCta heading={`ปรึกษาทนายผู้รับผิดชอบงาน${area.name}`} />
    </main>
  );
}
